// ============================================
// FILE: src/components/OffersScreen.jsx
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Heart, Plus, ShoppingCart, Clock, Zap, RefreshCw } from 'lucide-react';
import ProductDetailsModal from './modals/ProductDetailsModal';
import { getProductImage } from '../utils/imageHelpers';
import { getFinalPrice, hasVariants, getVariantPriceFrom } from '../utils/priceHelpers';
import apiService from '../apiService';
import QuantityTextBox from './QuantityTextBox';
import { usesQuantityTextBox } from '../utils/quantityHelpers';

const OffersScreen = ({
  initialSectionId = 'all',
  onBack,
  cart = [],
  addToCart,
  updateQuantity,
  getCartCount,
  getCartTotal,
  openCheckout,
  toggleFavorite,
  favorites = [],
  setShowSubscription,
  showToast,
  isDesktop = false,
  headerSlot = null,
  // Apparel needs the fashion detail screen (colour/size, media gallery).
  // When this is not supplied every product falls back to the grocery modal.
  onOpenFashionProduct = null,
}) => {
  // Own data state — fetched fresh on every open
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const [activeSection, setActiveSection] = useState(initialSectionId);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerLabel, setTimerLabel] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const timerRef = useRef(null);

  // Route apparel to the fashion detail screen; everything else keeps using
  // the existing product modal.
  const openProduct = (product) => {
    if (typeof onOpenFashionProduct === 'function' && onOpenFashionProduct(product)) {
      return;
    }
    setSelectedProduct(product);
    setShowProductDetails(true);
  };

  const favArray = Array.isArray(favorites) ? favorites : [];
  const isProductFavorited = (id) => favArray.some(f => String(f?.id) === String(id));

  // ── Fetch feature sections from API ─────────────────────────────────────
  const fetchSections = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const resp = await apiService.getFeatureSections();
      // apiService returns { success, status, data }
      // backend returns { sections: [...] }
      const raw = resp?.data?.sections || resp?.data || [];
      const list = Array.isArray(raw) ? raw : [];
      setSections(list);

      // If the initially requested section doesn't exist, fall back to 'all'
      if (initialSectionId !== 'all' && list.length > 0) {
        const exists = list.some(s => s.id === initialSectionId);
        if (!exists) setActiveSection('all');
      }
    } catch {
      setFetchError(true);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
    // Cleanup timer on unmount
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Countdown timer — earliest section with show_timer + end_at ─────────
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    const timerSections = sections.filter(s => s.show_timer && s.end_at);
    if (!timerSections.length) { setTimeLeft(null); return; }

    const earliest = timerSections.reduce((min, s) =>
      new Date(s.end_at) < new Date(min.end_at) ? s : min
    );
    setTimerLabel(earliest.name);

    const tick = () => {
      const diff = new Date(earliest.end_at) - new Date();
      if (diff <= 0) { setTimeLeft('00:00:00'); clearInterval(timerRef.current); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [sections]);

  // ── Deduplicated product list for selected section ───────────────────────
  const displayProducts = (() => {
    if (activeSection === 'all') {
      const seen = new Set();
      const result = [];
      for (const section of sections) {
        for (const product of (section.products || [])) {
          if (!seen.has(product.id)) {
            seen.add(product.id);
            result.push(product);
          }
        }
      }
      return result;
    }
    const section = sections.find(s => s.id === activeSection);
    return section?.products || [];
  })();

  const totalDeals = (() => {
    const seen = new Set();
    sections.forEach(s => (s.products || []).forEach(p => seen.add(p.id)));
    return seen.size;
  })();

  // ── Quantity helpers ─────────────────────────────────────────────────────
  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };
  const isWeightUnit = (unit) => ['kg', 'gram', 'g'].includes(String(unit || '').toLowerCase().trim());
  const formatUnit = (unit) => { const s = String(unit || '').trim(); return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase(); };
  const getQuantityOptions = (p) => isWeightUnit(getWeightParts(p).unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  const getSelectedQty = (id) => quantitySelections[id] || 1;
  const setSelectedQty = (id, qty) => setQuantitySelections(prev => ({ ...prev, [id]: qty }));

  // ── Skeleton ─────────────────────────────────────────────────────────────
  const Skeleton = () => (
    <div className={`grid gap-4 pt-2 ${isDesktop ? 'grid-cols-4 xl:grid-cols-5 max-w-[1280px] mx-auto' : 'grid-cols-2'}`}>
      {Array.from({ length: isDesktop ? 10 : 6 }).map((_, i) => (
        <div key={i} className="rounded overflow-hidden bg-white animate-pulse">
          <div className="h-44 bg-gray-200" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-8 bg-gray-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );

  const PillSkeleton = () => (
    <div className="flex gap-2 px-4 pb-3">
      {[80, 64, 72, 60].map(w => (
        <div key={w} className="h-7 rounded-full bg-gray-200 animate-pulse flex-shrink-0" style={{ width: w }} />
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[220] bg-white flex flex-col" style={{ maxWidth: isDesktop ? '100%' : 448, margin: '0 auto' }}>

      {/* Global desktop navbar (injected by App) */}
      {headerSlot}

      {/* ── Sticky Header ───────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-white" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
       <div className={isDesktop ? 'max-w-[1280px] mx-auto' : ''}>

        {/* Title row */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0">
            <ArrowLeft size={22} className="text-[#535766]" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#ff3f6c,#e03a60)' }}>
              <Zap size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-extrabold text-[#282c3f] leading-tight">Today's Deals</h1>
              {!loading && (
                <p className="text-[10px] text-[#94969f]">{totalDeals} deals available</p>
              )}
            </div>
          </div>
          {/* Countdown timer */}
          {timeLeft && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-[9px] text-[#94969f] font-medium uppercase tracking-wide flex items-center gap-0.5">
                <Clock size={9} /> Ends in
              </span>
              <span className="text-sm font-extrabold tabular-nums" style={{ color: '#ff3f6c' }}>{timeLeft}</span>
            </div>
          )}
        </div>

        {/* Section filter pills */}
        {loading && <PillSkeleton />}

        {!loading && sections.length > 0 && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto hide-scrollbar">
            <button
              onClick={() => setActiveSection('all')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex-shrink-0"
              style={activeSection === 'all'
                ? { background: 'linear-gradient(90deg,#ff3f6c,#e03a60)', color: '#fff' }
                : { background: '#f2f2f2', color: '#555' }}
            >
              <Zap size={11} />
              All Deals
            </button>
            {sections.map(section => {
              const isActive = activeSection === section.id;
              const color = section.color || '#3b82f6';
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0"
                  style={isActive ? { background: color, color: '#fff' } : { background: '#f2f2f2', color: '#555' }}
                >
                  {section.name}
                </button>
              );
            })}
          </div>
        )}
       </div>
      </div>

      {/* Product count + timer label */}
      {!loading && sections.length > 0 && (
        <div className={`px-4 py-2 flex items-center justify-between ${isDesktop ? 'max-w-[1280px] mx-auto w-full' : ''}`}>
          <span className="text-xs text-[#94969f]">
            {displayProducts.length} {displayProducts.length === 1 ? 'deal' : 'deals'}
            {activeSection !== 'all' && sections.find(s => s.id === activeSection)
              ? ` in ${sections.find(s => s.id === activeSection).name}` : ''}
          </span>
          {timeLeft && timerLabel && (
            <span className="text-[10px] text-[#ff3f6c] font-semibold bg-[#fff8fa] px-2 py-0.5 rounded-full border border-orange-200 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ff3f6c] animate-pulse inline-block" />
              {timerLabel}
            </span>
          )}
        </div>
      )}

      {/* ── Product grid ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 pb-4" style={{ background: '#f7f7f7' }}>

        {/* Loading skeleton */}
        {loading && <Skeleton />}

        {/* Fetch error */}
        {!loading && fetchError && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-5xl mb-4">⚠️</span>
            <p className="text-[#535766] font-semibold text-base">Couldn't load deals</p>
            <p className="text-[#94969f] text-sm mt-1 mb-5">Please check your connection</p>
            <button
              onClick={fetchSections}
              className="flex items-center gap-2 px-5 py-2.5 rounded text-sm font-semibold text-white"
              style={{ background: '#ff3f6c' }}
            >
              <RefreshCw size={14} /> Try Again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !fetchError && displayProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-6xl mb-4">🎁</span>
            <p className="text-[#94969f] font-semibold text-base">No deals right now</p>
            <p className="text-[#94969f] text-sm mt-1">Check back soon for new offers</p>
          </div>
        )}

        {/* Product cards */}
        {!loading && displayProducts.length > 0 && (
          <div className={`grid gap-4 pt-2 ${isDesktop ? 'grid-cols-4 xl:grid-cols-5 max-w-[1280px] mx-auto' : 'grid-cols-2'}`}>
            {displayProducts.map((product) => {
              const isFav = isProductFavorited(product.id);
              const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0
                && Number(product.sale_price) < Number(product.price);
              const offPercent = hasDiscount
                ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
                : 0;

              const ownerSection = sections.find(s => (s.products || []).some(p => p.id === product.id));
              const accentColor = ownerSection?.color || '#ff3f6c';

              return (
                <div
                  key={product.id}
                  className="rounded overflow-hidden bg-white shadow-sm"
                  style={{ borderTop: `3px solid ${accentColor}` }}
                >
                  {/* Product image */}
                  <div
                    className="relative overflow-hidden h-44 cursor-pointer group"
                    onClick={() => openProduct(product)}
                  >
                    {getProductImage(product, 'card')}
                    {hasDiscount && (
                      <span className="absolute top-3 left-3 text-xs bg-[#ff3f6c] text-white px-2 py-0.5 rounded-full font-bold shadow-sm ring-1 ring-white/70">
                        {offPercent}% OFF
                      </span>
                    )}
                    {ownerSection && activeSection === 'all' && (
                      <span
                        className="absolute bottom-2 left-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
                        style={{ background: accentColor + 'dd' }}
                      >
                        {ownerSection.name}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
                      className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
                    >
                      <Heart size={16} className={isFav ? 'text-pink-500' : 'text-[#94969f]'} fill={isFav ? 'currentColor' : 'none'} />
                    </button>
                  </div>

                  {/* Product info */}
                  <div className="p-3">
                    <h3
                      className="font-semibold text-[#282c3f] text-sm mb-2 line-clamp-2 cursor-pointer hover:text-[#ff3f6c] transition-colors leading-tight"
                      onClick={() => openProduct(product)}
                    >
                      {product.name}
                    </h3>

                    <div className="flex items-center gap-2 mb-3">
                      {hasVariants(product) ? (
                        <span className="text-sm font-extrabold text-emerald-600">from ₹{getVariantPriceFrom(product)}</span>
                      ) : hasDiscount ? (
                        <>
                          <span className="text-sm font-extrabold text-[#03a685]">₹{product.sale_price}</span>
                          <span className="text-xs text-[#94969f] line-through">₹{product.price}</span>
                        </>
                      ) : (
                        <span className="text-sm font-extrabold text-emerald-600">₹{getFinalPrice(product)}</span>
                      )}
                    </div>

                    {/* Quantity pills */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {usesQuantityTextBox(product) ? (
                        <QuantityTextBox
                          value={getSelectedQty(product.id)}
                          onChange={(qty) => setSelectedQty(product.id, qty)}
                        />
                      ) : (
                        getQuantityOptions(product).map((qty) => (
                          <button
                            key={qty}
                            onClick={() => setSelectedQty(product.id, qty)}
                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                              getSelectedQty(product.id) === qty
                                ? 'bg-[#ff3f6c] text-white border-[#ff3f6c]'
                                : 'bg-white text-[#535766] border-[#eaeaec] hover:border-[#ff3f6c]'
                            }`}
                          >
                            {qty}
                          </button>
                        ))
                      )}
                      {isWeightUnit(getWeightParts(product).unit) && (
                        <span className="text-xs text-[#94969f] font-medium self-center">Kg</span>
                      )}
                      {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                        <span className="text-xs text-[#94969f] font-medium self-center">{formatUnit(getWeightParts(product).unit)}</span>
                      )}
                    </div>

                    {/* Cart controls or Add button */}
                    {(() => {
                      const cartItem = cart.find(item => item.id === product.id);
                      if (cartItem && cartItem.quantity > 0) {
                        return (
                          <div className="w-full flex items-center justify-center gap-2 bg-[#fff8fa] border border-[#ff3f6c] rounded-lg px-2 py-1">
                            <button
                              className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] font-bold shadow-sm disabled:opacity-50"
                              disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                              onClick={async () => {
                                if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                  await updateQuantity(product.id, -1);
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                                }
                              }}
                            >&#8722;</button>
                            <span className="text-sm font-bold text-[#ff3f6c] w-8 text-center select-none">
                              {quantityLoading[product.id] ? '…' : cartItem.quantity}
                            </span>
                            <button
                              className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] font-bold shadow-sm disabled:opacity-50"
                              disabled={!!quantityLoading[product.id]}
                              onClick={async () => {
                                if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                  await updateQuantity(product.id, 1);
                                  setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                                }
                              }}
                            >&#43;</button>
                          </div>
                        );
                      }
                      return (
                        <button
                          onClick={() => addToCart(product, getSelectedQty(product.id))}
                          className="w-full text-white py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-1"
                          style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentColor}cc)` }}
                        >
                          <Plus size={14} /> Add to Cart
                        </button>
                      );
                    })()}

                    {Boolean(Number(product.is_subscribable)) && (
                      <button
                        onClick={() => setShowSubscription && setShowSubscription(product)}
                        className="w-full bg-[#03a685] text-white py-1.5 rounded-lg text-xs font-semibold hover:bg-[#028f73] transition-colors mt-2"
                      >
                        Subscribe &amp; Save
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom checkout bar — pinned inside this screen, no fixed overlap */}
      {cart.length > 0 && typeof getCartCount === 'function' && getCartCount() > 0 && (
        <div className={`px-4 py-3 bg-white border-t border-gray-100 ${isDesktop ? 'max-w-[1280px] mx-auto w-full' : ''}`} style={{ boxShadow: '0 -4px 16px rgba(0,0,0,0.08)' }}>
          <button
            onClick={openCheckout}
            className="w-full text-white py-3 rounded font-bold flex items-center justify-center gap-3 shadow-lg"
          >
            <ShoppingCart size={20} />
            <span>Checkout Now</span>
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
              {getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}
            </span>
          </button>
        </div>
      )}

      {/* Product details modal */}
      {showProductDetails && selectedProduct && (
        <ProductDetailsModal
          getCartCount={getCartCount}
          getCartTotal={getCartTotal}
          openCheckout={openCheckout}
          isOpen={showProductDetails}
          product={selectedProduct}
          onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
          addToCart={addToCart}
          cart={cart}
          updateQuantity={updateQuantity}
          toggleFavorite={toggleFavorite}
          favorites={favorites}
          isFavorited={isProductFavorited(selectedProduct?.id)}
          setShowSubscription={setShowSubscription}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default OffersScreen;
